import { AxiosInstance } from 'axios';
import MockAdapter from 'axios-mock-adapter';
import { isValue } from '../../helpers';
import { IMockProps, InitializeTypes } from '../../types';
import { Bundle, Mocks } from '../../util';
import { Logger } from '../logger';

export const configureMock = (
  instance: AxiosInstance,
  _config: InitializeTypes,
): string[] => {
  let lstMocksEnabled: string[] = [];

  if (_config.service.environment === 'MOCK') {
    const mockAdapter: MockAdapter = new MockAdapter(instance, {
      onNoMatch: 'passthrough',
      delayResponse: _config.service?.mockTimeout
        ? _config.service.mockTimeout
        : 1000,
    });
    Bundle.setValue('mockAdapter', mockAdapter);

    lstMocksEnabled = createMocks(mockAdapter);
  } else if (
    _config.globalMocks?.lstApis &&
    Array.isArray(_config.globalMocks.lstApis) &&
    _config.globalMocks.lstApis.length > 0
  ) {
    const mockAdapter: MockAdapter = new MockAdapter(instance, {
      onNoMatch: 'passthrough',
      delayResponse: isValue(_config.service.mockTimeout)
        ? _config.service.mockTimeout
        : 1000,
    });
    Bundle.setValue('mockAdapter', mockAdapter);
    lstMocksEnabled = createMocks(mockAdapter, _config.globalMocks.lstApis);
  }
  return lstMocksEnabled;
};

export const cleanMocks = (): boolean => {
  const mockAdapter = Bundle.getValue('mockAdapter');
  if (isValue(mockAdapter)) {
    try {
      mockAdapter.restore();
      mockAdapter.resetHandlers();
      mockAdapter.reset();
    } catch (error) {
      Logger.error(error);
    }
  }
  return true;
};

type MockMethodParamsType = {
  urlMock: string | RegExp;
  mockConfig: MockAdapter;
  mock: IMockProps;
  status: number;
  newData: unknown;
  newHeaders: unknown;
};

export const createMocks = (
  mockAdapter: MockAdapter,
  lstApisMock?: string[],
): string[] => {
  const lstMocksEnabled = [];
  for (const item of Mocks.getInstance().getAll()) {
    if (
      !item?.configRequest?.path ||
      (isValue(lstApisMock) &&
        Array.isArray(lstApisMock) &&
        lstApisMock.indexOf(item.configRequest.path) === -1)
    ) {
      continue;
    }

    if (lstMocksEnabled.indexOf(item.configRequest.path) === -1) {
      lstMocksEnabled.push(item.configRequest.path);
    }

    const urlMock = new RegExp(`${item.configRequest.path}$`);
    const { status, data, headers, withoutData = false } = item.configResponse;
    const newHeaders = { ...headers, mockEnabled: true };
    const paramsMock: MockMethodParamsType = {
      urlMock,
      status,
      newHeaders,
      newData: withoutData ? data : { data: data },
      mockConfig: mockAdapter,
      mock: item,
    };

    switch (item.configRequest.method) {
      case 'POST': {
        createMockPutPostPatch('onPost', paramsMock);
        break;
      }
      case 'PUT': {
        createMockPutPostPatch('onPut', paramsMock);
        break;
      }
      case 'GET': {
        createMockGet(paramsMock);
        break;
      }
      case 'DELETE': {
        createMockDelete(paramsMock);
        break;
      }
      case 'PATCH': {
        createMockPutPostPatch('onPatch', paramsMock);
        break;
      }
      default: {
        break;
      }
    }
  }
  return lstMocksEnabled;
};

const createMockPutPostPatch = (
  httpVerb: 'onPost' | 'onPut' | 'onPatch',
  mockParams: MockMethodParamsType,
) => {
  const { urlMock, mockConfig, mock, status, newData, newHeaders } = mockParams;
  const body = mock.configRequest.body;

  mockConfig[httpVerb](urlMock, body || undefined).reply(
    status,
    newData,
    newHeaders,
  );
};

const createMockGet = (mockParams: MockMethodParamsType) => {
  const { urlMock, mockConfig, mock, status, newData, newHeaders } = mockParams;
  const params = mock.configRequest.params;
  mockConfig
    .onGet(
      urlMock,
      params ? { params: params } : undefined,
      mock.configRequest.headers && !params
        ? {
            Accept: 'application/json, text/plain, */*',
            ...mock.configRequest.headers,
          }
        : undefined,
    )
    .reply(status, newData, newHeaders);
};

const createMockDelete = (mockParams: MockMethodParamsType) => {
  const { urlMock, mockConfig, mock, status, newData, newHeaders } = mockParams;
  mockConfig
    .onDelete(
      urlMock,
      mock.configRequest?.params
        ? { params: mock.configRequest.params }
        : mock.configRequest?.body,
    )
    .reply(status, newData, newHeaders);
};
